import React, { useCallback, useEffect, useState } from 'react';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { DatePicker } from '../components/ui/DatePicker';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { ReceiptIcon, DollarSignIcon, ShieldOffIcon, RefreshCwIcon, CalendarIcon, AlertCircleIcon } from 'lucide-react';
type DatePreset = 'this_year' | 'last_year' | 'custom';
// Helper: format date as mm-dd-yyyy for API
const formatDateForAPI = (dateStr: string): string => {
  const d = new Date(dateStr);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};
// Get date range for presets
const getDateRange = (preset: DatePreset): {
  from: string;
  to: string;
} => {
  const now = new Date();
  if (preset === 'this_year') {
    return {
      from: `${now.getFullYear()}-01-01`,
      to: now.toISOString().split('T')[0]
    };
  }
  if (preset === 'last_year') {
    const lastYear = now.getFullYear() - 1;
    return {
      from: `${lastYear}-01-01`,
      to: `${lastYear}-12-31`
    };
  }
  return {
    from: '',
    to: ''
  };
};
export function SalesTax() {
  const {
    selectedCompanyId
  } = useData();
  // Filter state
  const [preset, setPreset] = useState<DatePreset>('this_year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Data state
  const [taxableSales, setTaxableSales] = useState(0);
  const [taxCollected, setTaxCollected] = useState(0);
  const [nonTaxableSales, setNonTaxableSales] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  // Get effective date range
  const getEffectiveDateRange = useCallback(() => {
    if (preset === 'custom') {
      return {
        from: customFrom,
        to: customTo
      };
    }
    return getDateRange(preset);
  }, [preset, customFrom, customTo]);
  // Fetch all paid invoices and compute tax totals
  const fetchSalesTaxData = useCallback(async () => {
    if (!selectedCompanyId) return;
    const {
      from,
      to
    } = getEffectiveDateRange();
    if (!from || !to) return;
    setIsLoading(true);
    setError(null);
    setProgress(null);
    setTaxableSales(0);
    setTaxCollected(0);
    setNonTaxableSales(0);
    setInvoiceCount(0);
    try {
      const result = await callBillingAPI('getInvoicesAggregation', {
        company_id: selectedCompanyId,
        status: 'paid',
        from_date: formatDateForAPI(from),
        to_date: formatDateForAPI(to)
      });
      setTaxableSales(parseFloat(result.taxable_amount) || 0);
      setNonTaxableSales(parseFloat(result.non_taxable_amount) || 0);
      setTaxCollected(parseFloat(result.tax_amount) || 0);
      setInvoiceCount(parseInt(result.total_count) || 0);
    } catch (err) {
      console.error('Failed to fetch sales tax data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sales tax data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, getEffectiveDateRange]);
  // Fetch on mount and when company/filters change
  useEffect(() => {
    fetchSalesTaxData();
  }, [selectedCompanyId, preset]);
  // For custom dates, fetch when both dates are set
  useEffect(() => {
    if (preset === 'custom' && customFrom && customTo) {
      fetchSalesTaxData();
    }
  }, [customFrom, customTo]);
  const handlePresetChange = (newPreset: DatePreset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      setCustomFrom('');
      setCustomTo('');
    }
  };
  // Get display label for current date range
  const getDateRangeLabel = () => {
    const {
      from,
      to
    } = getEffectiveDateRange();
    if (!from || !to) return '';
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const fmt = (d: Date) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `${fmt(fromDate)} – ${fmt(toDate)}`;
  };
  return <div className="space-y-6" data-id="element-2582">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2583">
        <div data-id="element-2584">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2585">Sales Tax Report</h1>
          <p className="text-gray-500 mt-1" data-id="element-2586">
            {getDateRangeLabel() && <span className="flex items-center gap-1.5" data-id="element-2587">
                <CalendarIcon className="w-4 h-4" data-id="element-2588" />
                {getDateRangeLabel()}
                {invoiceCount > 0 && !isLoading && <span className="text-gray-400 ml-1" data-id="element-2589">
                    · {invoiceCount} paid invoice{invoiceCount !== 1 ? 's' : ''}
                  </span>}
              </span>}
          </p>
        </div>
        <Button variant="secondary" onClick={fetchSalesTaxData} disabled={isLoading} leftIcon={<RefreshCwIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-2591" />} data-id="element-2590">
          Refresh
        </Button>
      </div>

      {/* Date Filters */}
      <Card data-id="element-2592">
        <div className="space-y-4" data-id="element-2593">
          <div className="flex flex-wrap gap-2" data-id="element-2594">
            <Button variant={preset === 'this_year' ? 'primary' : 'secondary'} size="sm" onClick={() => handlePresetChange('this_year')} data-id="element-2595">
              This Year
            </Button>
            <Button variant={preset === 'last_year' ? 'primary' : 'secondary'} size="sm" onClick={() => handlePresetChange('last_year')} data-id="element-2596">
              Last Year
            </Button>
            <Button variant={preset === 'custom' ? 'primary' : 'secondary'} size="sm" onClick={() => handlePresetChange('custom')} data-id="element-2597">
              Custom Dates
            </Button>
          </div>

          {preset === 'custom' && <div className="flex flex-col sm:flex-row gap-4 pt-2 border-t border-gray-200" data-id="element-2598">
              <div className="w-full sm:w-48" data-id="element-2599">
                <DatePicker label="From Date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} data-id="element-2600" />
              </div>
              <div className="w-full sm:w-48" data-id="element-2601">
                <DatePicker label="To Date" value={customTo} onChange={e => setCustomTo(e.target.value)} data-id="element-2602" />
              </div>
            </div>}
        </div>
      </Card>

      {/* Progress / Loading */}
      {isLoading && progress && <Card data-id="element-2603">
          <div className="flex items-center gap-3 py-2" data-id="element-2604">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" data-id="element-2605"></div>
            <p className="text-sm text-gray-600" data-id="element-2606">{progress}</p>
          </div>
        </Card>}

      {/* Error */}
      {error && <Card data-id="element-2607">
          <div className="flex items-start gap-3 p-2" data-id="element-2608">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2609">
              <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-2610" />
            </div>
            <div data-id="element-2611">
              <p className="text-sm font-medium text-gray-900" data-id="element-2612">
                Failed to load sales tax data
              </p>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-2613">{error}</p>
              <Button size="sm" onClick={fetchSalesTaxData} className="mt-2" data-id="element-2614">
                Retry
              </Button>
            </div>
          </div>
        </Card>}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2615">
        <StatCard title="Total Taxable Sales" value={isLoading && invoiceCount === 0 ? '—' : formatCurrency(taxableSales)} icon={<DollarSignIcon className="w-6 h-6" data-id="element-2617" />} data-id="element-2616" />
        <StatCard title="Sales Tax Collected" value={isLoading && invoiceCount === 0 ? '—' : formatCurrency(taxCollected)} icon={<ReceiptIcon className="w-6 h-6" data-id="element-2619" />} className={taxCollected > 0 ? 'border-green-200 bg-green-50' : ''} data-id="element-2618" />
        <StatCard title="Total Non-Taxable Sales" value={isLoading && invoiceCount === 0 ? '—' : formatCurrency(nonTaxableSales)} icon={<ShieldOffIcon className="w-6 h-6" data-id="element-2621" />} data-id="element-2620" />
      </div>

      {/* Summary */}
      {!isLoading && !error && invoiceCount > 0 && <Card data-id="element-2622">
          <CardHeader title="Summary" data-id="element-2623" />
          <div className="space-y-3" data-id="element-2624">
            <div className="flex justify-between text-sm" data-id="element-2625">
              <span className="text-gray-600" data-id="element-2626">Total Taxable Sales</span>
              <span className="font-medium text-gray-900" data-id="element-2627">
                {formatCurrency(taxableSales)}
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-2628">
              <span className="text-gray-600" data-id="element-2629">Sales Tax Collected</span>
              <span className="font-medium text-green-600" data-id="element-2630">
                {formatCurrency(taxCollected)}
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-2631">
              <span className="text-gray-600" data-id="element-2632">Total Non-Taxable Sales</span>
              <span className="font-medium text-gray-900" data-id="element-2633">
                {formatCurrency(nonTaxableSales)}
              </span>
            </div>
            <div className="border-t border-gray-200 pt-3" data-id="element-2634">
              <div className="flex justify-between text-sm font-semibold" data-id="element-2635">
                <span className="text-gray-900" data-id="element-2636">Total Sales (All)</span>
                <span className="text-gray-900" data-id="element-2637">
                  {formatCurrency(taxableSales + nonTaxableSales)}
                </span>
              </div>
            </div>
            {taxableSales > 0 && <div className="pt-2" data-id="element-2638">
                <div className="flex justify-between text-xs" data-id="element-2639">
                  <span className="text-gray-500" data-id="element-2640">Effective Tax Rate</span>
                  <span className="text-gray-500" data-id="element-2641">
                    {(taxCollected / taxableSales * 100).toFixed(2)}%
                  </span>
                </div>
              </div>}
          </div>
        </Card>}

      {/* Empty state */}
      {!isLoading && !error && invoiceCount === 0 && <Card data-id="element-2642">
          <div className="py-12 text-center" data-id="element-2643">
            <ReceiptIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2644" />
            <p className="text-gray-500 font-medium" data-id="element-2645">No paid invoices found</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-2646">
              Try adjusting the date range or check that invoices are marked as
              paid.
            </p>
          </div>
        </Card>}
    </div>;
}